/**
 * Pagination Component
 *
 * A custom pagination control with previous/next buttons and a page number input.
 * Provides accessibility support for screen readers via aria-live announcements.
 *
 * ACCESSIBILITY FIX (March 2026):
 * Previously, aria-live was placed directly on the visible TextInputControl which
 * contained translated text (ofLabel from react-i18next). This caused NVDA to announce
 * the page position twice when the language changed because:
 * 1. I18nextProvider updates trigger a re-render with new translated text
 * 2. MuxProvider lang prop update triggers another re-render
 * Each render cycle caused the aria-live region to announce.
 *
 * Solution: Use a separate hidden aria-live region that only updates on explicit
 * user page navigation actions (clicking prev/next or entering a page number).
 * This prevents announcements on language change while preserving accessibility
 * for page navigation.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { handle, noop, uniqueId, useTheme,
  INPUT_VARIANT, TextInput, TEXT_ALIGN } from '@manulife/mux';
import { useTranslation } from 'react-i18next';
import { FasatIcon } from '../../../components';
import { staticCommonLabelKeys } from '../../../moduleConstants';

import { Nav, ButtonControl, ScreenReaderOnly, TextInputControl } from './styledComponents';

const PX_REM_RATIO = 0.0625;
const rem = (px) => `${px * PX_REM_RATIO}rem`;
const remTwentyEight = 28;
const ANNOUNCEMENT_DELAY_MS = 50;

/**
 * Display left arrow icon
 */
const displayLeftArrowIcon = (leftAltText, item, theme) => {
  const isDisabled = (item === 1 || item === 0);
  return (
    <FasatIcon
      alt={leftAltText}
      manulifeName="buttonlefticon"
      fill={
      isDisabled
        ? theme.colorPalette.neutralColors.light.shade_dark_3
        : theme.colorPalette.neutralColors.dark.shade_light_1
      }
      height={rem(remTwentyEight)}
      width={rem(remTwentyEight)}
    />
  );
};

/**
 * Display right arrow icon
 */
const displayRightArrowIcon = (rightAltText, item, theme, totalItems) => (
  <FasatIcon
    alt={rightAltText}
    manulifeName="buttonrighticon"
    fill={
            item === totalItems
              ? theme.colorPalette.neutralColors.light.shade_dark_3
              : theme.colorPalette.neutralColors.dark.shade_light_1
          }
    height={rem(remTwentyEight)}
    width={rem(remTwentyEight)}
  />
);

function Pagination({
  id = uniqueId('pagination-id-'),
  currentItem,
  totalItems,
  previousAltText,
  nextAltText,
  onChange,
  navigationAriaLabel,
  currentItemAriaLabel,
}) {
  const theme = useTheme();
  const [t] = useTranslation();
  const ofLabel = t(staticCommonLabelKeys.COMMON_LABEL_OF);
  const [item, setItem] = useState(currentItem);
  const [text, setText] = useState(`${currentItem}`);
  const wrappedOnChange = handle(onChange);

  /**
   * Screen Reader Announcement State
   *
   * We maintain a separate hidden aria-live region for screen reader announcements
   * instead of placing aria-live on the visible TextInputControl.
   *
   * WHY: The visible control contains translated text (ofLabel) which updates on
   * language change. React-i18next and MuxProvider update in separate render cycles,
   * causing aria-live to announce twice when language changes.
   *
   * HOW: This announcement state only updates when:
   * - User clicks previous/next buttons
   * - User enters a page number and blurs the input
   *
   * RESULT: Screen readers announce "Page X of Y" only on user navigation,
   * not on language change.
   */
  const [announcement, setAnnouncement] = useState('');
  const pendingAnnouncementRef = useRef(null);

  /**
   * Announce page position to screen readers.
   *
   * Uses a clear-then-set pattern with a short delay to ensure screen readers
   * detect the content change even if navigating to the same announcement text.
   * The debouncing prevents multiple rapid announcements from overlapping.
   *
   * @param {number} newItem - The new page number
   * @param {number} total - Total number of pages
   */
  const announce = useCallback((newItem, total) => {
    // Clear any pending announcement to prevent duplicates
    if (pendingAnnouncementRef.current) {
      clearTimeout(pendingAnnouncementRef.current);
    }
    // Clear first, then set after short delay to ensure screen reader picks up change
    // (same text won't be re-announced without clearing first)
    setAnnouncement('');
    pendingAnnouncementRef.current = setTimeout(() => {
      setAnnouncement(`${newItem} ${ofLabel} ${total}`);
    }, ANNOUNCEMENT_DELAY_MS);
  }, [ofLabel]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (pendingAnnouncementRef.current) {
      clearTimeout(pendingAnnouncementRef.current);
    }
  }, []);

  useEffect(() => {
    setText(`${currentItem}`);
    setItem(currentItem);
  }, [currentItem]);

  const textChangeHandler = useCallback(
    (value, event) => {
      const parsedValue = parseInt(value, 10);
      if (parsedValue && parsedValue >= 1 && parsedValue <= totalItems) {
        // Only trigger onChange if the value actually changed
        if (parsedValue !== item) {
          setItem(parsedValue);
          setText(`${parsedValue}`);
          wrappedOnChange(event, parsedValue);
          announce(parsedValue, totalItems);
        }
      } else {
        setText(`${item}`);
      }
    },
    [totalItems, item, setItem, wrappedOnChange, setText, announce],
  );

  // Use props or fallback to translated labels
  const leftAltText = useMemo(() => previousAltText || t(staticCommonLabelKeys.ARIA_COMMON_PAGINATION_BUTTON_PREVIOUS), [
    previousAltText,
    t,
  ]);
  const rightAltText = useMemo(() => nextAltText || t(staticCommonLabelKeys.ARIA_COMMON_PAGINATION_BUTTON_NEXT), [
    nextAltText,
    t,
  ]);

  /**
   * Controller for chevron toggles
   * @param {number} shift - +1 move forward by one item, -1 move back by one
   */
  const pageControlHandler = useCallback((event, shift) => {
    const newItem = shift + item;
    if (newItem >= 1 && newItem <= totalItems) {
      setItem(newItem);
      setText(`${newItem}`);
      wrappedOnChange(event, newItem);
      announce(newItem, totalItems);
    }
  }, [item, totalItems, wrappedOnChange, setItem, announce]);
  const handlePreviousClick = useCallback((event) => pageControlHandler(event, -1), [pageControlHandler]);
  const handleNextClick = useCallback((event) => pageControlHandler(event, +1), [pageControlHandler]);
  const handleOnChange = useCallback((value) => { setText(value); }, []);
  const handleOnBlur = useCallback((e) => textChangeHandler(text, e), [text, textChangeHandler]);

  return (
    <Nav id={id} aria-label={navigationAriaLabel}>
      {/*
       * Hidden aria-live region for screen reader announcements.
       *
       * - aria-live="polite": Waits for user to finish current task before announcing
       * - aria-atomic="true": Announces entire content, not just changed parts
       *
       * This region is updated ONLY on user page navigation actions:
       * - Click previous/next buttons
       * - Enter page number in input and blur
       *
       * NOT updated on language change to prevent NVDA double announcement issue.
       */}
      <ScreenReaderOnly aria-live="polite" aria-atomic="true">
        {announcement}
      </ScreenReaderOnly>
      <ButtonControl
        disabled={item === 1 || item === 0}
        aria-disabled={item === 1 || item === 0}
        onClick={handlePreviousClick}
        data-testid="previous"
      >
        {displayLeftArrowIcon(leftAltText, item, theme)}
        <ScreenReaderOnly>{leftAltText}</ScreenReaderOnly>
      </ButtonControl>
      <TextInputControl>
        {/*
          Note: MUX TextInput onBlur provides only the event, not the latest input value.
          To commit manual page changes, pass the current text state into the blur handler.
        */}
        <TextInput
          value={text}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          variant={INPUT_VARIANT.BASIC_LINE}
          textAlign={TEXT_ALIGN.CENTER}
          customStyle={{ rootStyle: { maxWidth: '55px' } }}
          aria-label={currentItemAriaLabel}
          data-testid="current-item-input"
        />
        {ofLabel}
&nbsp;
        <span data-testid="total-pages">
          {totalItems}
&nbsp;
        </span>
      </TextInputControl>
      <ButtonControl
        disabled={item === totalItems}
        aria-disabled={item === totalItems}
        onClick={handleNextClick}
        data-testid="next"
      >
        {displayRightArrowIcon(rightAltText, item, theme, totalItems)}
        <ScreenReaderOnly>{rightAltText}</ScreenReaderOnly>
      </ButtonControl>
    </Nav>
  );
}

Pagination.propTypes = {
  /** DOM element ID to give the pagination control */
  id: PropTypes.string,
  /** The currently selected item */
  currentItem: PropTypes.number,
  /** Total number of items */
  totalItems: PropTypes.number,
  /** previous alt text */
  previousAltText: PropTypes.string,
  /** next alt text */
  nextAltText: PropTypes.string,
  /** onChange function that gets called back with new item */
  onChange: PropTypes.func,
  /** aria-label for pagination control */
  navigationAriaLabel: PropTypes.string.isRequired,
  /** aria-label for input that has current item */
  currentItemAriaLabel: PropTypes.string.isRequired,
};

Pagination.defaultProps = {
  id: undefined,
  currentItem: 1,
  totalItems: 1,
  previousAltText: '',
  nextAltText: '',
  onChange: noop,
};

Pagination.displayName = 'Pagination';

export default Pagination;
